import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Character limit constants for form fields
export const CHAR_LIMITS = {
  NAME: 50,
  EMAIL: 100,
  PHONE: 20,
  SUBJECT: 100,
  COMPANY: 50,
  MESSAGE: 1000,
  NOTES: 500,
  ADDRESS: 200,
}

/**
 * Validates if a string contains only normal characters
 * Allows: Letters (a-z, A-Z), numbers, spaces, and common punctuation
 * Rejects: Non-Latin characters, emojis, and special symbols
 */
export function containsOnlyNormalCharacters(input: string): boolean {
  // Regular expression to match basic Latin characters, numbers, spaces and common punctuation
  const normalCharPattern = /^[a-zA-Z0-9 áéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ.,;:'"!?@#$%&*()_+\-=[\]{}|\\/<>]+$/;
  
  return normalCharPattern.test(input);
}

/**
 * Sanitizes a string to keep only normal characters
 * Removes any characters that don't match the normal pattern
 * @param input The input string to sanitize
 * @param maxLength Optional maximum length to limit the output
 * @returns The sanitized string
 */
export function sanitizeToNormalCharacters(input: string, maxLength?: number): string {
  if (!input) return '';
  
  // Remove any characters that aren't in the allowed set
  const sanitized = input.replace(/[^\w áéíóúÁÉÍÓÚäëïöüÄËÏÖÜñÑ.,;:'"!?@#$%&*()_+\-=[\]{}|\\/<>]/g, '');
  
  // Apply length limit if provided
  if (maxLength && sanitized.length > maxLength) {
    return sanitized.substring(0, maxLength);
  }
  
  return sanitized;
}

// Re-export for fixing any import issues
export { sanitizeToNormalCharacters as sanitizeInput };

/**
 * Format a number as Argentine Peso (ARS)
 * @param amount The number to format as currency
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
  }).format(amount);
}

/**
 * Format a number as US Dollars (USD)
 * @param amount The number to format as currency
 * @returns Formatted currency string
 */
export function formatUsdCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Convert USD amount to ARS using the exchange rate
 * @param amountUsd Amount in US Dollars
 * @param exchangeRate Exchange rate (ARS per 1 USD)
 * @returns Amount in ARS
 */
export function convertUsdToArs(amountUsd: number, exchangeRate: number): number {
  if (!exchangeRate || exchangeRate <= 0) {
    console.error('Invalid exchange rate provided');
    return 0;
  }
  return amountUsd * exchangeRate;
}

/**
 * Fetch the current exchange rate from the API
 * @returns Promise resolving to the exchange rate info including rate, isFallback and bcraApiFailed flags
 */
export async function fetchExchangeRate(): Promise<{ rate: number, isFallback: boolean, bcraApiFailed: boolean }> {
  // Helper to format date as YYYY-MM-DD
  function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  const today = new Date();
  // Try up to 7 previous days
  for (let i = 0; i < 7; i++) {
    const tryDate = new Date(today);
    tryDate.setDate(today.getDate() - i);
    const dateStr = formatDate(tryDate);
    try {
      const response = await fetch(`/api/currency?fecha=${dateStr}`);
      if (!response.ok) throw new Error('Failed to fetch exchange rate');
      const data = await response.json();
      if (data && data.rate && data.success !== false) {
        return {
          rate: data.rate,
          isFallback: data.fallback === true || data.success === false,
          bcraApiFailed: data.bcraApiFailed === true
        };
      }
    } catch (error) {
      // Log and try previous day
      console.warn(`Exchange rate fetch failed for ${dateStr}:`, error);
    }
  }
  // If all attempts fail, return fallback
  return { rate: 1000, isFallback: true, bcraApiFailed: true };
} 